// Recurrence helpers shared by CalendarWidget + ReminderBell.
// A reminder is stored with a base `date` and a `recurrence` in
// {"none","daily","weekly","monthly","yearly"}.

export const RECURRENCE_OPTIONS = [
  { value: "none",    label: "Doesn't repeat" },
  { value: "daily",   label: "Every day" },
  { value: "weekly",  label: "Every week" },
  { value: "monthly", label: "Every month" },
  { value: "yearly",  label: "Every year" },
];

export const RECURRENCE_LABEL = Object.fromEntries(
  RECURRENCE_OPTIONS.map((o) => [o.value, o.label])
);

export function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const parseISO = (s) => new Date(`${s}T00:00`);

/**
 * Does `reminder` occur on `dateStr` (YYYY-MM-DD)?
 * Recurring reminders occur on every matching date on or after their base date.
 */
export function occursOn(reminder, dateStr) {
  const rec = reminder.recurrence || "none";
  if (rec === "none") return reminder.date === dateStr;
  const base = parseISO(reminder.date);
  const target = parseISO(dateStr);
  if (target < base) return false;
  if (rec === "daily") return true;
  if (rec === "weekly") return base.getDay() === target.getDay();
  if (rec === "monthly") return base.getDate() === target.getDate();
  if (rec === "yearly")
    return base.getDate() === target.getDate() && base.getMonth() === target.getMonth();
  return false;
}

/**
 * Expand recurring reminders into occurrences within [fromDate, toDate]
 * (both inclusive, JS Date objects). Non-recurring reminders are included
 * as-is if their date falls in the window.
 * Each result carries the parent reminder + an `occurrence_date` key.
 */
export function expandOccurrences(reminders, fromDate, toDate) {
  const out = [];
  const from = new Date(fromDate); from.setHours(0, 0, 0, 0);
  const to = new Date(toDate); to.setHours(0, 0, 0, 0);
  for (const r of reminders || []) {
    const rec = r.recurrence || "none";
    const base = parseISO(r.date);
    if (rec === "none") {
      if (base >= from && base <= to) {
        out.push({ ...r, occurrence_date: r.date, is_recurring: false });
      }
      continue;
    }
    // Walk from max(base, from) to `to`, stepping by the pattern.
    let d = new Date(Math.max(base.getTime(), from.getTime()));
    // Align first candidate onto the pattern.
    if (rec === "weekly") {
      while (d.getDay() !== base.getDay()) d.setDate(d.getDate() + 1);
    } else if (rec === "monthly") {
      const dom = base.getDate();
      if (d.getDate() > dom) { d.setMonth(d.getMonth() + 1); }
      d.setDate(dom);
    } else if (rec === "yearly") {
      const m = base.getMonth(), dom = base.getDate();
      if (d.getMonth() > m || (d.getMonth() === m && d.getDate() > dom)) {
        d.setFullYear(d.getFullYear() + 1);
      }
      d.setMonth(m);
      d.setDate(dom);
    }
    // Safety cap so a malformed pattern can't infinite-loop.
    let guard = 0;
    while (d <= to && guard < 5000) {
      out.push({ ...r, occurrence_date: dateKey(d), is_recurring: true });
      if (rec === "daily") d.setDate(d.getDate() + 1);
      else if (rec === "weekly") d.setDate(d.getDate() + 7);
      else if (rec === "monthly") d.setMonth(d.getMonth() + 1);
      else if (rec === "yearly") d.setFullYear(d.getFullYear() + 1);
      else break;
      guard += 1;
    }
  }
  return out;
}
